/**
 * SBI header bar – master design for dashboard and all SBI pages (except landing).
 * Logo, optional right content, "Back to Menu" link, dark/light theme switch.
 * Supports Fresenius Kabi branding (from contextBranding) on all pages.
 */

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import { useMaterialUIController, setSbiDashboardTheme } from "context";
import PropTypes from "prop-types";
import colors from "assets/theme/base/colors";
import { useBranding, FRESENIUS } from "contextBranding";

function SbiHeader({ rightContent }) {
  const { t } = useTranslation();
  const [controller, dispatch] = useMaterialUIController();
  const { sbiDashboardTheme } = controller;
  const { isFreseniusBranding, appendBrandingSearch } = useBranding();
  const isLight = sbiDashboardTheme === "light";
  const theme = isLight ? colors.sbiDashboardTheme.light : colors.sbiDashboardTheme.dark;

  const freseniusTheme = isLight ? FRESENIUS.themeLight : FRESENIUS.themeDark;
  const headerBg = isFreseniusBranding ? freseniusTheme.bgHeader : theme.bgHeader;
  const headerBorder = isFreseniusBranding ? freseniusTheme.border : theme.border;
  const linkColor = isFreseniusBranding ? freseniusTheme.link : theme.link;

  const handleToggleTheme = () => {
    setSbiDashboardTheme(dispatch, isLight ? "dark" : "light");
  };

  return (
    <MDBox
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      gap={2}
      sx={{
        backgroundColor: headerBg,
        borderBottom: `1px solid ${headerBorder}`,
        py: 2,
        px: { xs: 2, md: 3 },
      }}
    >
      <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
        {isFreseniusBranding && (
          <MDBox
            component="img"
            src={`${process.env.PUBLIC_URL || ""}/${isLight ? "fresenius_logo.png" : "fresenius_logo_white.png"}`}
            alt="Fresenius Kabi"
            sx={{ height: 32, width: "auto", objectFit: "contain" }}
          />
        )}
        <MDBox
          component="img"
          src={`${process.env.PUBLIC_URL || ""}/${isLight ? "logo_with_text.svg" : "logo_with_text_light.svg"}`}
          alt="Safe Brain Initiative"
          sx={{ height: 36, width: "auto", objectFit: "contain" }}
        />
      </MDBox>
      <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
        {rightContent}
        <Link
          to={appendBrandingSearch("/landing")}
          style={{
            color: linkColor,
            textDecoration: "none",
            fontSize: "0.875rem",
          }}
        >
          &larr; {t("common.backToMenu")}
        </Link>
        <Tooltip title={isLight ? t("common.switchToDarkMode") : t("common.switchToLightMode")}>
          <IconButton
            onClick={handleToggleTheme}
            size="small"
            sx={{
              color: linkColor,
              "&:hover": {
                backgroundColor: isLight ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.08)",
              },
            }}
            aria-label={isLight ? t("common.switchToDarkMode") : t("common.switchToLightMode")}
          >
            <Icon fontSize="small">
              {isLight ? "dark_mode" : "light_mode"}
            </Icon>
          </IconButton>
        </Tooltip>
      </MDBox>
    </MDBox>
  );
}

SbiHeader.propTypes = {
  rightContent: PropTypes.node,
};

SbiHeader.defaultProps = {
  rightContent: null,
};

export default SbiHeader;
